import _ from 'lodash';
import { IDataRef } from '../TreeStore/DataRef';
import { IExerciseModel } from '../Exercise/ExerciseModel';
import { IFeature } from '../Feature/FeatureModel';
import { IPathModel } from '../Path/PathModel';
import { IQuestionModel } from '../Question/QuestionModel';
import { IRootStore } from '../Root/RootStore';
import { ISession } from '../Session/SessionModel';
import { ISessionStepModel } from '../Session/SessionStepModel';
import { ISurvey } from '../Survey/SurveyModel';
import { ITreeNav } from './TreeNav';
import { Instance, SnapshotOut, getRoot, types } from 'mobx-state-tree';

export const TreeNavContent = types.model('TreeNavContent')
  .views(self => ({
    get contentId(): string | undefined {
      const dataRefType = this.currentDataRef?.type;
      if (dataRefType === 'SESSION_STEP') {
        return this.sessionStep?.contentId || undefined;
      } else {
        return this.currentDataRef?.dataId;
      }
    },
    get treeDataRef(): IDataRef | undefined {
      const rootStore = getRoot<IRootStore>(self);
      const treeNav = rootStore.treeNav as ITreeNav;
      return treeNav.tree?.dataRef;
    },
    get currentDataRef(): IDataRef | undefined {
      const rootStore = getRoot<IRootStore>(self);
      const treeNav = rootStore.treeNav as ITreeNav;
      return treeNav.currentNode?.dataRef || undefined;
    },
    get treeDataType(): string | undefined {
      return this.treeDataRef?.type;
    },
    /**
     * The block (survey) the current node belongs to. Read from the node
     * meta, so it is known even before the survey object itself is loaded.
     */
    get surveyId(): string | undefined {
      if (this.treeDataType === 'SURVEY') {
        return this.treeDataRef?.dataId;
      }
      const rootStore = getRoot<IRootStore>(self);
      const treeNav = rootStore.treeNav as ITreeNav;
      return treeNav.currentNode?.surveyId || undefined;
    },
    get survey(): ISurvey | undefined {
      if (this.treeDataType === 'SURVEY') {
        return this.treeDataRef?.survey;
      } else if (this.treeDataType === 'SESSION') {
        // the surveyId is stored in tree node metadata (not as a dataRef)
        const rootStore = getRoot<IRootStore>(self);
        const treeNav = rootStore.treeNav as ITreeNav;
        return treeNav.currentNode?.survey;
      }
      return undefined;
    },
    get session(): ISession | undefined {
      return this.treeDataRef?.session;
    },
    get path(): IPathModel | undefined {
      const rootStore = getRoot<IRootStore>(self);
      const pathId = this.session?.path;
      return pathId
        ? rootStore.path.getPath(pathId) || undefined
        : undefined;
    },
    get feature(): IFeature | undefined {
      return this.currentDataRef?.feature;
    },
    get stepType(): string | undefined {
      if (this.currentDataRef?.type === 'SESSION_STEP') {
        return this.sessionStep?.contentType;
      } else {
        return this.currentDataRef?.type;
      }
    },
    get sessionStep(): ISessionStepModel | undefined {
      return this.currentDataRef?.sessionStep;
    },
    get exercise(): IExerciseModel | undefined {
      const contentId = this.sessionStep?.contentType === 'EXERCISE' ? this.sessionStep?.contentId : undefined;
      return contentId
        ? getRoot<IRootStore>(self).exercise.getExercise(contentId) || undefined
        : undefined;
    },
    get question(): SnapshotOut<IQuestionModel> | undefined {
      const questionStore = getRoot<IRootStore>(self).question;
      const dataRefType = this.currentDataRef?.type;
      let questionId;
      if (dataRefType === 'SESSION_STEP') {
        questionId = this.sessionStep?.contentId;
        if (!questionId) {
          return undefined;
        }
        const question = questionStore.getQuestion(questionId);
        return question || undefined;
      } else {
        // withRegion, not the raw question: this branch feeds the instant-session
        // wizard, which was rendering a literal "{{region}}" to the user because
        // only the SESSION_STEP branch above went through the substitution.
        // Cloned first so the store's own snapshot is not mutated.
        const question = this.currentDataRef?.question;
        return question ? questionStore.withRegion(_.cloneDeep(question)) : undefined;
      }
    },
    get nextQuestion(): IQuestionModel | undefined {
      const survey = this.survey;
      if (!survey) {
        return undefined;
      }
      const rootStore = getRoot<IRootStore>(self);
      const treeNav = rootStore.treeNav as ITreeNav;
      if (!treeNav.next) {
        return undefined;
      }
      // the next node asks the question directly (its key may be prefixed)
      const nextNode = treeNav.tree?.findNodeByKey(treeNav.next);
      return nextNode?.dataRef?.question
        || rootStore.question.getQuestionByKey(nextNode?.questionKey || treeNav.next, survey._id);
    },
  }));

export type ITreeNavContent = Instance<typeof TreeNavContent>;
